import {
    FETCH_DAILY_JET_LAG_STATUS_START,
    FETCH_DAILY_JET_LAG_STATUS_SUCCESS,
    FETCH_DAILY_JET_LAG_STATUS_FAIL
} from 'actions/ActionTypes.js';
import { STATUS_CODE_NO_CONTENT } from 'JetLag/jetLagConstants';
import { getCurrentDaysFlights } from 'JetLag/jetLagUtils';
import { oneDecimalRound } from 'utils/NumberUtils';
import { createSelector } from 'reselect';

/** @typedef {import('reducers/multiSportEdit/types/entity/RootState').RootState} RootState */

/**
 * @typedef {{
 *  jetLagDailyStatus: { [key: string]: any };
 *  statusCodesByDate: { [key: string]: number };
 *  jetLagDailyStatusFetching: boolean;
 *  jetLagDailyStatusFetchFail: boolean;
 * }} JetLagDailyStatusState
 */

/** @type {JetLagDailyStatusState} */
const INITIAL_STATE = {
    jetLagDailyStatus: {},
    statusCodesByDate: {},
    jetLagDailyStatusFetching: false,
    jetLagDailyStatusFetchFail: false
};

// ======= Fetching Handlers =======

/**
 * @param {JetLagDailyStatusState} state
 * @returns {JetLagDailyStatusState}
 */
const fetchDailyJetLagStatusFetching = (state) => {
    return {
        ...state,
        jetLagDailyStatusFetching: true,
        jetLagDailyStatusFetchFail: false
    };
};

/**
 * @param {JetLagDailyStatusState} state
 * @param {ReturnType<typeof fetchDailyJetLagStatusSuccess>} action
 * @returns {JetLagDailyStatusState}
 */
const fetchDailyJetLagStatusSuccessActionHandler = (state, action) => {
    const { calendarDate, data, status } = action.payload;

    data.overlappingFlights = getCurrentDaysFlights(data.flights, data.calendarDate);

    return {
        jetLagDailyStatus: {
            ...state.jetLagDailyStatus,
            [calendarDate]: data
        },
        statusCodesByDate: {
            ...state.statusCodesByDate,
            [calendarDate]: status
        },
        jetLagDailyStatusFetching: false,
        jetLagDailyStatusFetchFail: false
    };
};

/**
 * @param {JetLagDailyStatusState} state
 * @returns {JetLagDailyStatusState}
 */
const fetchDailyJetLagStatusFail = (state) => {
    return {
        ...state,
        jetLagDailyStatusFetching: false,
        jetLagDailyStatusFetchFail: true
    };
};

// ======= Selectors =======
export const getDailyJetLagStatus = (state, calendarDate) => {
    return state.jetLagDailyStatus.jetLagDailyStatus[calendarDate]
        ? state.jetLagDailyStatus.jetLagDailyStatus[calendarDate]
        : {};
};

/**
 * @param {RootState} state
 * @returns {JetLagDailyStatusState}
 */
export const selectJetLagDailyStatus = (state) => state.jetLagDailyStatus;

/**
 * @param {RootState} state
 * @param {string} calendarDate
 */
export const selectStatusCodeForDate = (state, calendarDate) => {
    return state.jetLagDailyStatus.statusCodesByDate[calendarDate];
};

export const getDailyJetLagStatusWithBodyClockInHours = (state, calendarDate) => {
    const dailyStatus = state.jetLagDailyStatus.jetLagDailyStatus[calendarDate];

    if (!dailyStatus) {
        return {};
    }

    const statusInHours = {
        ...dailyStatus,
        // minutes to hours, with at most one decimal
        bodyClockDifference:
            dailyStatus.bodyClockDifference !== null ? oneDecimalRound(dailyStatus.bodyClockDifference / 60) : null
    };
    return statusInHours;
};

/** is there jet leg data fetched for props.date */
export const selectIsJetLagDataForDate = createSelector(
    selectJetLagDailyStatus,
    selectStatusCodeForDate,
    (state, status) => {
        const isStatusValid = status && status !== STATUS_CODE_NO_CONTENT;
        const isData = !state.jetLagDailyStatusFetching && !state.jetLagDailyStatusFetchFail && isStatusValid;

        return isData;
    }
);

export const isDailyJetLagStatusFetching = (state) => {
    return state.jetLagDailyStatus.jetLagDailyStatusFetching;
};

export const didDailyJetLagStatusFetchFail = (state) => {
    return state.jetLagDailyStatus.jetLagDailyStatusFetchFail;
};

// ======= Handler Assignments =======
const handlers = {
    [FETCH_DAILY_JET_LAG_STATUS_START]: fetchDailyJetLagStatusFetching,
    [FETCH_DAILY_JET_LAG_STATUS_SUCCESS]: fetchDailyJetLagStatusSuccessActionHandler,
    [FETCH_DAILY_JET_LAG_STATUS_FAIL]: fetchDailyJetLagStatusFail
};

// ======= Default Action =======
export default function jetLagDailyStatus(state = INITIAL_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}
